import { Box, Typography, Alert } from '@mui/material'
import { Button, Input } from 'Common/Supplyvue-ui'
import { LoadingButton } from '@mui/lab'
import { useState, useEffect } from 'react'
import { VariantFormProps } from '../types'
import { PlanStartWeekInput, VariantPrivacySelector } from './index'
// Import other form components from parent directory
import { PlanHorizonInputs } from '../../PlanHorizonInputs'
import { ConstrainingHorizonInputs } from '../../ConstrainingHorizonInputs'
import { PlanAccessSelector } from '../../PlanAccessSelector'
import { WorkcentreModal } from '../../../WorkcentreModal/WorkcentreModal.view'
import {
  SelectedWorkcentreDetails,
  SourceSiteDetails,
} from '../../../WorkcentreModal/WorkcentreModal.types'
import { ConstrainingHorizonsModal } from '../../ConstrainingHorizonsModal/ConstrainingHorizonsModal'
import {
  processSelectedSourceSites,
  flattenWorkcentresForTable,
  unflattenWorkcentresFromTable,
  WorkcentreTableRow,
} from '../../utils/planHelpers'
import { PLAN_DATA_ACTIONS } from '../../planDataReducer'

const horizonSelectionMode = {
  weeks: 'weeks',
  date: 'date',
} as const

export const VariantForm = ({
  createPlanDataObject,
  dispatch,
  sourceSiteData,
  mode,
  selectedVariantName,
  variantPrivacyType,
  onVariantPrivacyTypeChange,
  onSave,
  onCancel,
  isLoading = false,
  horizonsSelectionModeState,
  setHorizonsSelectionModeState,
}: VariantFormProps) => {
  const [variantName, setVariantName] = useState<string>(
    mode === 'edit' ? selectedVariantName || '' : ''
  )

  // Update variant name when selectedVariantName changes in edit mode
  useEffect(() => {
    if (mode === 'edit') {
      setVariantName(selectedVariantName || '')
    }
  }, [selectedVariantName, mode])
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [isConstrainingHorizonsModalOpen, setIsConstrainingHorizonsModalOpen] =
    useState<boolean>(false)

  const handleVariantNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVariantName(e.target.value)
  }

  const handleActionClick = () => {
    setIsModalOpen(true)
  }

  const getSelectedSites = (sourceSiteData: SourceSiteDetails[]) => {
    const selectedSourceSites = processSelectedSourceSites(
      sourceSiteData,
      createPlanDataObject.constrainingHorizon
    )
    dispatch({
      type: PLAN_DATA_ACTIONS.SET_SOURCE_SITES,
      payload: selectedSourceSites,
    })
    setIsModalOpen(false)
  }

  const handleAccessTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch({
      type: PLAN_DATA_ACTIONS.SET_PLAN_ACCESS_TYPE,
      payload: event.target.value,
    })
  }

  const handleUpdateConstrainingHorizons = () => {
    setIsConstrainingHorizonsModalOpen(true)
  }

  const handleSaveConstrainingHorizons = (
    updatedData: WorkcentreTableRow[]
  ) => {
    const updatedSourceSites = unflattenWorkcentresFromTable(
      updatedData,
      createPlanDataObject.sourceSites
    )

    dispatch({
      type: PLAN_DATA_ACTIONS.UPDATE_WORKCENTRE_CONSTRAINING_DATA,
      payload: updatedSourceSites,
    })

    setIsConstrainingHorizonsModalOpen(false)
  }

  const handleSave = () => {
    if (variantName.trim()) {
      onSave(variantName.trim())
    }
  }

  const isFormValid = () => {
    return (
      variantName.trim().length > 0 &&
      createPlanDataObject.planStartWeek &&
      createPlanDataObject.planHorizon > 0 &&
      createPlanDataObject.constrainingHorizon > 0 &&
      createPlanDataObject.sourceSites.length > 0
    )
  }

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto' }}>
      {/* Variant Name Input */}
      <Box
        display="flex"
        flexDirection="row"
        marginTop={2}
        justifyContent="space-between"
      >
        <Box display="flex" alignItems="center" width="20%">
          <Typography>Variant name</Typography>
        </Box>
        <Box marginLeft={3}>
          <Input
            sx={{ width: '300px' }}
            value={variantName}
            onChange={handleVariantNameChange}
            disabled={isLoading || mode === 'edit'}
            placeholder="Enter variant name"
          />
        </Box>
        <Box display="flex" alignItems="center" width="20%"></Box>
      </Box>

      {/* Privacy Selector */}
      <Box sx={{ mt: 2 }}>
        <VariantPrivacySelector
          value={variantPrivacyType}
          onChange={onVariantPrivacyTypeChange}
          disabled={isLoading}
        />
      </Box>

      {/* Form Inputs */}
      <PlanStartWeekInput
        createPlanDataObject={createPlanDataObject}
        dispatch={dispatch}
        isPlanCreating={isLoading}
        horizonsSelectionModeState={horizonsSelectionModeState}
        setHorizonsSelectionModeState={setHorizonsSelectionModeState}
        horizonSelectionMode={horizonSelectionMode}
        selectedTemplateKey={null}
        setIsSelectedVariantEdited={() => {}}
      />

      <PlanHorizonInputs
        createPlanDataObject={createPlanDataObject}
        createPlanData={{} as any}
        dispatch={dispatch}
        isPlanCreating={isLoading}
        horizonsSelectionModeState={horizonsSelectionModeState}
        setHorizonsSelectionModeState={setHorizonsSelectionModeState}
        horizonSelectionMode={horizonSelectionMode}
        selectedTemplateKey={null}
        setIsSelectedVariantEdited={() => {}}
      />

      <ConstrainingHorizonInputs
        createPlanDataObject={createPlanDataObject}
        createPlanData={{} as any}
        dispatch={dispatch}
        isPlanCreating={isLoading}
        horizonsSelectionModeState={horizonsSelectionModeState}
        setHorizonsSelectionModeState={setHorizonsSelectionModeState}
        horizonSelectionMode={horizonSelectionMode}
        selectedTemplateKey={null}
        setIsSelectedVariantEdited={() => {}}
      />

      <PlanAccessSelector
        value={createPlanDataObject.planAccessType}
        onChange={handleAccessTypeChange}
        disabled={isLoading}
      />

      {/* Source Sites Section */}
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        marginTop={2}
      >
        <Box display="flex" alignItems="center" width="20%">
          <Typography>Source sites</Typography>
        </Box>
        <Box marginLeft={3} display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={handleActionClick}
            disabled={isLoading}
          >
            Select source sites ({createPlanDataObject.sourceSites.length})
          </Button>
          {/* {createPlanDataObject.sourceSites.length > 0 && (
            <Button
              variant="outlined"
              onClick={handleUpdateConstrainingHorizons}
              disabled={isLoading}
            >
              Update constraining horizons
            </Button>
          )} */}
        </Box>
        <Box display="flex" alignItems="center" width="20%"></Box>
      </Box>

      {/* Validation Messages */}
      {!isFormValid() && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Please fill in all required fields and select at least one source
          site.
        </Alert>
      )}

      {/* Action Buttons */}
      <Box
        display="flex"
        justifyContent="flex-end"
        gap={2}
        marginTop={4}
        sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}
      >
        <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={isLoading}
          disabled={!isFormValid()}
        >
          {mode === 'edit' ? 'Update variant' : 'Save variant'}
        </LoadingButton>
      </Box>

      {/* Modals */}
      {isModalOpen && (
        <WorkcentreModal
          isModalOpen={isModalOpen}
          handleModalClose={() => setIsModalOpen(false)}
          handleOkbuttonClick={getSelectedSites}
          isUpdating={false}
          readOnly={false}
          sourceSiteData={sourceSiteData ?? []}
          templateSourceSiteDetails={createPlanDataObject.sourceSites ?? []}
        />
      )}

      {isConstrainingHorizonsModalOpen && (
        <ConstrainingHorizonsModal
          isOpen={isConstrainingHorizonsModalOpen}
          onClose={() => setIsConstrainingHorizonsModalOpen(false)}
          onSave={handleSaveConstrainingHorizons}
          tableData={flattenWorkcentresForTable(createPlanDataObject)}
          planStartWeek={createPlanDataObject.planStartWeek as Date}
          isLoading={false}
        />
      )}
    </Box>
  )
}
